// src/pages/admin/AdminBatches.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Row, Col, Card, Button, Modal, Form, ListGroup, Badge, Table, Spinner
} from "react-bootstrap";
import {
  addGroup,
  getGroups,
  getGroupMembers,
  addGroupMembers,
  getStudents,
  getUsers,
  addBatch,
  getBatches
} from "../../service/apiService";

/**
 * AdminBatches (fixed show group members)
 */
function AdminBatches({ user }) {
  // Groups
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);

  // Selected (queued) students for the group (client-side)
  const [selectedStudentsForGroup, setSelectedStudentsForGroup] = useState([]);

  // Students (from students table)
  const [allStudents, setAllStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  // Users & staff (from users table)
  const [staffList, setStaffList] = useState([]);

  // Batches
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(true);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [batchForm, setBatchForm] = useState({
    title: "", description: "", start_date: "", end_date: "",
    schedules: [{ day_of_week: 1, start_time: "10:00", end_time: "12:00" }],
    student_ids: [], group_ids: [], assigned_staff: []
  });

  // Loading helpers
  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const res = await getGroups();
      if (res && res.status === "success") setGroups(res.data);
      else if (Array.isArray(res)) setGroups(res);
      else setGroups([]);
    } catch (err) {
      console.error("loadGroups error:", err);
      setGroups([]);
    }
    setLoadingGroups(false);
  }, []);

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const res = await getBatches();
      if (res && res.status === "success") setBatches(res.data);
      else if (Array.isArray(res)) setBatches(res);
      else setBatches([]);
    } catch (err) {
      console.error("loadBatches error:", err);
      setBatches([]);
    }
    setLoadingBatches(false);
  }, []);

  // initial load: groups, batches, students, staff
  useEffect(() => {
    loadGroups();
    loadBatches();

    (async () => {
      // students from students table
      setLoadingStudents(true);
      try {
        const sres = await getStudents();
        const studentsArr = (sres && sres.status === 'success') ? sres.data : (Array.isArray(sres) ? sres : []);
        setAllStudents(studentsArr.map(s => ({ id: Number(s.id), name: s.name || s.full_name || `${s.first_name || ''} ${s.last_name || ''}`, email: s.email || s.contact_email || '', raw: s })));
        console.log("Loaded students:", studentsArr.length);
      } catch (err) {
        console.error("Error loading students:", err);
        setAllStudents([]);
      }
      setLoadingStudents(false);

      // load users for staff list
      try {
        const r = await getUsers();
        const arr = Array.isArray(r) ? r : (r && r.status === "success" ? r.data : []);
        const normalized = (arr || []).map(u => ({
          id: Number(u.id),
          name: u.name,
          email: u.email,
          role: (u.role || '').toString().toLowerCase(),
          position: (u.position || '').toString().toLowerCase(),
          raw: u
        }));
        const staffs = normalized.filter(u => ['staff','trainer'].includes(u.role) || u.position.includes('staff') || u.position.includes('trainer'));
        setStaffList(staffs);
        console.log("Loaded staff:", staffs.length);
      } catch (e) {
        console.error("Failed to load users/staff:", e);
        setStaffList([]);
      }
    })();
  }, [loadGroups, loadBatches]);

  // open group and load its members (robust)
  const openGroup = async (g) => {
    setSelectedGroup(g);
    setSelectedStudentsForGroup([]); // clear queued selections when opening group
    setGroupMembers([]);

    try {
      const res = await getGroupMembers(g.id);
      console.log("getGroupMembers raw response:", res);

      // Handle different shapes:
      // 1) { status:'success', data: [ {id,name,email,added_at}, ... ] }
      // 2) raw array [ {id, name, email, added_at}, ... ]
      // 3) array of user objects with different field names
      let members = [];
      if (!res) {
        members = [];
      } else if (Array.isArray(res)) {
        members = res;
      } else if (res.status === "success" && Array.isArray(res.data)) {
        members = res.data;
      } else if (res.data && Array.isArray(res.data)) {
        members = res.data;
      } else {
        // try to salvage if res has a nested payload
        const maybe = res.rows || res.members || res.data || [];
        members = Array.isArray(maybe) ? maybe : [];
      }

      // Normalize each member to { id, name, email, added_at }
      const normalized = members.map((m) => {
        // m may be { id, name } or { student_id, full_name } etc.
        const id = Number(m.id || m.student_id || m.user_id || 0);
        const name = m.name || m.full_name || `${m.first_name || ''} ${m.last_name || ''}` || m.username || 'Unknown';
        const email = m.email || m.contact_email || m.phone || '';
        const added_at = m.added_at || m.created_at || m.joined_at || null;
        return { id, name: name.trim(), email, added_at };
      }).filter(x => x.id > 0);

      setGroupMembers(normalized);

      // If API returned member_count or other meta, attach it to selectedGroup object for display
      const enrichedGroup = { ...g };
      if (typeof g.member_count === 'undefined') {
        enrichedGroup.member_count = normalized.length;
      }
      setSelectedGroup(enrichedGroup);
    } catch (err) {
      console.error("getGroupMembers/error:", err);
      setGroupMembers([]);
    }
  };

  // create new group
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return alert("Enter group name");
    try {
      const res = await addGroup({ name: newGroupName.trim(), description: "", created_by: user.id });
      if (res && res.status === "success") {
        setShowNewGroup(false);
        setNewGroupName("");
        await loadGroups();
      } else {
        alert(res.message || "Failed to create group");
      }
    } catch (err) {
      console.error("addGroup error:", err);
      alert("Error creating group");
    }
  };

  // ---------- Selected (queued) students logic ----------
  const handleSelectStudentForGroup = (studentId) => {
    const sid = Number(studentId);
    if (!sid) return;
    const alreadyMember = groupMembers.some(gm => Number(gm.id) === sid);
    const alreadySelected = selectedStudentsForGroup.some(s => Number(s.id) === sid);
    if (alreadyMember || alreadySelected) return;
    const studentObj = allStudents.find(s => Number(s.id) === sid);
    if (!studentObj) return;
    setSelectedStudentsForGroup(prev => [...prev, { id: sid, name: studentObj.name, email: studentObj.email }]);
  };

  const handleRemoveSelectedStudent = (studentId) => {
    setSelectedStudentsForGroup(prev => prev.filter(s => Number(s.id) !== Number(studentId)));
  };

  const handleSaveSelectedStudentsToGroup = async () => {
    if (!selectedGroup) return alert("Select a group first");
    if (selectedStudentsForGroup.length === 0) return alert("No students selected");
    try {
      const payload = {
        group_id: selectedGroup.id,
        student_ids: selectedStudentsForGroup.map(s => s.id)
      };
      const res = await addGroupMembers(payload);
      if (res && res.status === "success") {
        // refresh group members and groups summary
        const r2 = await getGroupMembers(selectedGroup.id);
        if (r2 && r2.status === "success") setGroupMembers(r2.data.map(m => ({
          id: Number(m.id || m.student_id || m.user_id),
          name: m.name || m.full_name,
          email: m.email || ''
        })));
        await loadGroups();
        setSelectedStudentsForGroup([]);
        alert(`${res.added || selectedStudentsForGroup.length} students added to the group`);
      } else {
        alert(res.message || "Failed to add members");
      }
    } catch (err) {
      console.error("Error saving group members:", err);
      alert("Error while saving");
    }
  };

  // ---------------- Batch creation logic (unchanged) ----------------
  const openCreateBatch = () => {
    setBatchForm({
      title: "", description: "", start_date: "", end_date: "",
      schedules: [{ day_of_week: 1, start_time: "10:00", end_time: "12:00" }],
      student_ids: [], group_ids: [], assigned_staff: []
    });
    setShowCreateBatch(true);
  };

  const addScheduleRow = () => {
    setBatchForm(prev => ({ ...prev, schedules: [...prev.schedules, { day_of_week: 1, start_time: "10:00", end_time: "12:00" }] }));
  };
  const removeScheduleRow = (idx) => {
    setBatchForm(prev => ({ ...prev, schedules: prev.schedules.filter((_,i)=>i!==idx) }));
  };
  const toggleStudentInBatch = (id) => {
    setBatchForm(prev => {
      const exists = prev.student_ids.includes(id);
      return { ...prev, student_ids: exists ? prev.student_ids.filter(x=>x!==id) : [...prev.student_ids, id] };
    });
  };
  const toggleGroupInBatch = (id) => {
    setBatchForm(prev => {
      const exists = prev.group_ids.includes(id);
      return { ...prev, group_ids: exists ? prev.group_ids.filter(x=>x!==id) : [...prev.group_ids, id] };
    });
  };
  const toggleStaffAssignment = (id) => {
    setBatchForm(prev => {
      const exists = prev.assigned_staff.includes(id);
      return { ...prev, assigned_staff: exists ? prev.assigned_staff.filter(x=>x!==id) : [...prev.assigned_staff, id] };
    });
  };

  const handleCreateBatchSubmit = async () => {
    if (!batchForm.title.trim()) return alert("Batch title required");
    try {
      const payload = {
        ...batchForm,
        created_by: user.id
      };
      payload.schedules = payload.schedules.map(s => ({ day_of_week: parseInt(s.day_of_week), start_time: s.start_time, end_time: s.end_time }));
      const res = await addBatch(payload);
      if (res && res.status === "success") {
        setShowCreateBatch(false);
        await loadBatches();
        await loadGroups();
      } else {
        alert(res.message || "Failed creating batch");
      }
    } catch (err) {
      console.error("addBatch error:", err);
      alert("Error creating batch");
    }
  };

  return (
    <div className="p-3">
      <Row>
        <Col md={4}>
          <Card className="p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Student Groups</h5>
              <Button size="sm" onClick={()=>setShowNewGroup(true)}>+ Group</Button>
            </div>

            {loadingGroups ? (
              <div className="text-center py-3"><Spinner /></div>
            ) : (
              <ListGroup>
                {groups.map(g => (
                  <ListGroup.Item key={g.id} action onClick={()=>openGroup(g)} active={selectedGroup && selectedGroup.id===g.id}>
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{g.name}</strong>
                        <div className="small text-muted">{typeof g.member_count !== 'undefined' ? g.member_count : '—'} students</div>
                      </div>
                      <div className="text-muted small">{g.created_at ? new Date(g.created_at).toLocaleDateString() : ''}</div>
                    </div>
                  </ListGroup.Item>
                ))}
                {groups.length === 0 && <ListGroup.Item className="text-muted">No groups found</ListGroup.Item>}
              </ListGroup>
            )}
          </Card>

          {/* Group Members card with selected queued list */}
          <Card className="p-3">
            <h6>Group Members</h6>

            {!selectedGroup ? (
              <p className="text-muted">Select a group to view members</p>
            ) : (
              <>
                <div className="mb-2 d-flex justify-content-between align-items-start">
                  <div>
                    <strong>{selectedGroup.name}</strong>
                    <div className="small text-muted">{selectedGroup.member_count || groupMembers.length || 0} students</div>
                  </div>
                  <div className="text-muted small">{selectedGroup.created_at ? new Date(selectedGroup.created_at).toLocaleDateString() : ''}</div>
                </div>

                {/* Saved members */}
                <div className="mb-2">
                  <div className="small text-muted mb-1">Saved members</div>
                  <ListGroup className="mb-2">
                    {groupMembers.length === 0 ? (
                      <ListGroup.Item className="text-muted">No members in this group</ListGroup.Item>
                    ) : (
                      groupMembers.map(m => (
                        <ListGroup.Item key={m.id}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{m.name}</strong> <div className="small text-muted">{m.email}</div>
                            </div>
                            <div className="small text-muted">Added: {m.added_at ? new Date(m.added_at).toLocaleDateString() : '—'}</div>
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </div>

                {/* Dropdown to select students to add (client-side only) */}
                <div className="mb-2">
                  <Form.Label className="small">Select student to add (will be queued)</Form.Label>
                  <Form.Select onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!v) return;
                      handleSelectStudentForGroup(v);
                      e.target.selectedIndex = 0;
                    }}>
                    <option value="">-- select student --</option>
                    {allStudents
                      .filter(s => !groupMembers.some(gm => Number(gm.id) === Number(s.id)) && !selectedStudentsForGroup.some(ss => Number(ss.id) === Number(s.id)))
                      .map(s => (
                        <option key={s.id} value={s.id}>{s.name} {s.email ? `(${s.email})` : ''}</option>
                      ))}
                  </Form.Select>
                </div>

                {/* Selected queued students with remove */}
                <div className="mb-2">
                  <div className="small text-muted mb-1">Selected students (not yet saved)</div>
                  {selectedStudentsForGroup.length === 0 ? (
                    <div className="text-muted small">No students selected</div>
                  ) : (
                    <ListGroup className="mb-2">
                      {selectedStudentsForGroup.map(s => (
                        <ListGroup.Item key={s.id} className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{s.name}</strong> <div className="small text-muted">{s.email}</div>
                          </div>
                          <div>
                            <Button size="sm" variant="outline-danger" onClick={() => handleRemoveSelectedStudent(s.id)}>Remove</Button>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </div>

                {/* Save / Clear */}
                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={() => setSelectedStudentsForGroup([])}>Clear</Button>
                  <Button variant="primary" onClick={handleSaveSelectedStudentsToGroup} disabled={selectedStudentsForGroup.length === 0}>Save Selected to Group</Button>
                </div>
              </>
            )}
          </Card>
        </Col>

        <Col md={8}>
          <div className="d-flex justify-content-between mb-2">
            <h5>Batches</h5>
            <div>
              <Button onClick={openCreateBatch}>+ Create Batch</Button>
            </div>
          </div>

          <Card className="p-3">
            {loadingBatches ? <div className="text-center py-3"><Spinner/></div> : (
              <>
                <Table striped hover responsive>
                  <thead><tr><th>#</th><th>Title</th><th>Schedule</th><th>Students</th><th>Groups</th><th>Staff</th></tr></thead>
                  <tbody>
                    {batches.map((b,i)=>(
                      <tr key={b.id}>
                        <td>{i+1}</td>
                        <td><strong>{b.title}</strong><div className="small text-muted">{b.description}</div></td>
                        <td>
                          {b.schedules && b.schedules.length ? b.schedules.map(s => (
                            <div key={s.id}><strong>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][s.day_of_week]}</strong> {s.start_time} - {s.end_time}</div>
                          )) : <div className="small text-muted">No schedule</div>}
                        </td>
                        <td>{(b.students||[]).length}</td>
                        <td>{(b.groups||[]).map(g=> <Badge key={g.id} bg="light" text="dark" className="me-1">{g.name}</Badge>)}</td>
                        <td>{(b.staff||[]).map(s=> <div key={s.id}>{s.name} <small className="text-muted">({s.role||'trainer'})</small></div>)}</td>
                      </tr>
                    ))}
                    {batches.length === 0 && <tr><td colSpan={6} className="text-center text-muted">No batches found</td></tr>}
                  </tbody>
                </Table>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* New Group modal */}
      <Modal show={showNewGroup} onHide={()=>setShowNewGroup(false)} centered>
        <Modal.Header closeButton><Modal.Title>Create Group</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Group Name</Form.Label>
            <Form.Control value={newGroupName} onChange={(e)=>setNewGroupName(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowNewGroup(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup}>Create</Button>
        </Modal.Footer>
      </Modal>

      {/* Create Batch modal */}
      <Modal show={showCreateBatch} onHide={()=>setShowCreateBatch(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Create Batch</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Title</Form.Label>
            <Form.Control value={batchForm.title} onChange={(e)=>setBatchForm({...batchForm, title:e.target.value})} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={batchForm.description} onChange={(e)=>setBatchForm({...batchForm, description:e.target.value})} />
          </Form.Group>

          <Row className="mb-2">
            <Col md={6}><Form.Group><Form.Label>Start Date</Form.Label><Form.Control type="date" value={batchForm.start_date} onChange={(e)=>setBatchForm({...batchForm, start_date:e.target.value})} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>End Date</Form.Label><Form.Control type="date" value={batchForm.end_date} onChange={(e)=>setBatchForm({...batchForm, end_date:e.target.value})} /></Form.Group></Col>
          </Row>

          <div className="mb-2">
            <strong>Schedules</strong>
            {batchForm.schedules.map((s, idx) => (
              <Row key={idx} className="align-items-center mb-1">
                <Col md={3}>
                  <Form.Select value={s.day_of_week} onChange={(e)=> {
                    const v = Number(e.target.value);
                    setBatchForm(prev => ({ ...prev, schedules: prev.schedules.map((x,i)=>i===idx?{...x,day_of_week:v}:x) }));
                  }}>
                    <option value={0}>Sun</option><option value={1}>Mon</option><option value={2}>Tue</option><option value={3}>Wed</option>
                    <option value={4}>Thu</option><option value={5}>Fri</option><option value={6}>Sat</option>
                  </Form.Select>
                </Col>
                <Col md={3}><Form.Control type="time" value={s.start_time} onChange={(e)=> {
                  const v = e.target.value;
                  setBatchForm(prev => ({ ...prev, schedules: prev.schedules.map((x,i)=>i===idx?{...x,start_time:v}:x) }));
                }} /></Col>
                <Col md={3}><Form.Control type="time" value={s.end_time} onChange={(e)=> {
                  const v = e.target.value;
                  setBatchForm(prev => ({ ...prev, schedules: prev.schedules.map((x,i)=>i===idx?{...x,end_time:v}:x) }));
                }} /></Col>
                <Col md={3}><Button variant="outline-danger" onClick={()=>removeScheduleRow(idx)}>Remove</Button></Col>
              </Row>
            ))}
            <Button size="sm" onClick={addScheduleRow}>+ Add Schedule</Button>
          </div>

          <hr />
          <Row>
            <Col md={6}>
              <h6>Add Groups</h6>
              <div style={{maxHeight:200, overflow:'auto', paddingRight:8}}>
                {groups.map(g => (
                  <div key={g.id}><Form.Check inline label={`${g.name} (${g.member_count||0})`} type="checkbox" checked={batchForm.group_ids.includes(g.id)} onChange={()=>toggleGroupInBatch(g.id)} /></div>
                ))}
              </div>
            </Col>
            <Col md={6}>
              <h6>Add Students</h6>
              <div style={{maxHeight:200, overflow:'auto', paddingRight:8}}>
                {loadingStudents ? <Spinner/> : allStudents.map(s => (
                  <div key={s.id}><Form.Check inline label={s.name} type="checkbox" checked={batchForm.student_ids.includes(s.id)} onChange={()=>toggleStudentInBatch(s.id)} /></div>
                ))}
              </div>
            </Col>
          </Row>

          <hr />
          <h6>Assign Staff</h6>
          <div style={{maxHeight:180, overflow:'auto', paddingRight:8}}>
            {staffList.map(st => (
              <div key={st.id}><Form.Check inline label={`${st.name} (${st.position||st.role})`} type="checkbox" checked={batchForm.assigned_staff.includes(st.id)} onChange={()=>toggleStaffAssignment(st.id)} /></div>
            ))}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setShowCreateBatch(false)}>Cancel</Button>
          <Button onClick={handleCreateBatchSubmit}>Create Batch</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminBatches;
